// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Custom Embla scrollbar with a draggable thumb.
 *
 * Two progress modes are supported:
 *
 * - `"stepper"`    — thumb snaps from one `scrollSnap` to the next. Best for
 *                    paginated carousels.
 * - `"percentage"` — thumb tracks continuous scroll progress. Best for
 *                    `dragFree` carousels.
 *
 * Thumb position is written directly to DOM style for 60fps smoothness;
 * React state is intentionally avoided in the hot path.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CarouselInstance } from "@stoneage/App/Components/EmblaCarousel/models";
import React, { FC, useCallback, useEffect, useRef } from "react";

/** Strategy used to drive the scrollbar thumb. */
export type ScrollbarMode = "percentage" | "stepper";

/** Props for the {@link Scrollbar} component. */
export interface ScrollbarProps {
    carouselApi: CarouselInstance | null;
    isVertical: boolean;
    /** Allow grabbing and dragging the thumb. */
    isDraggable: boolean;
    /** Progress strategy. Defaults to `"stepper"`. */
    mode?: ScrollbarMode;
}

/** Minimum thumb size (as a % of the track) so it never disappears on huge lists. */
const MIN_THUMB_PERCENT = 5;

interface ScrollbarMetrics {
    maxScroll: number;
    thumbSizePercent: number;
    thumbSizePx: number;
    draggableSpacePx: number;
    trackRect: DOMRect;
}

export const Scrollbar: FC<ScrollbarProps> = ({ carouselApi, isVertical, isDraggable, mode = "stepper" }) => {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const thumbRef = useRef<HTMLDivElement | null>(null);

    const isPercentageMode = mode === "percentage";
    const axis = isVertical ? "vertical" : "horizontal";

    /** Measure the track and compute thumb sizing. */
    const getMetrics = useCallback((): ScrollbarMetrics | null => {
        if (!carouselApi || !trackRef.current) return null;

        const engine = carouselApi.internalEngine();
        const container = carouselApi.containerNode();

        const maxScroll = engine.limit.length;
        const clientSize = isVertical ? container.clientHeight : container.clientWidth;
        const totalContentSize = maxScroll + clientSize;

        const trackRect = trackRef.current.getBoundingClientRect();
        const trackSize = isVertical ? trackRect.height : trackRect.width;

        const thumbSizePercent =
            totalContentSize > 0 ? Math.max(MIN_THUMB_PERCENT, (clientSize / totalContentSize) * 100) : 100;
        const thumbSizePx = trackSize * (thumbSizePercent / 100);
        const draggableSpacePx = trackSize - thumbSizePx;

        return { maxScroll, thumbSizePercent, thumbSizePx, draggableSpacePx, trackRect };
    }, [carouselApi, isVertical]);

    /** Write thumb size and position directly to its style attribute. */
    const applyThumbStyle = useCallback(
        (progress: number) => {
            if (!thumbRef.current) return;
            const metrics = getMetrics();
            if (!metrics) return;

            const { maxScroll, thumbSizePercent } = metrics;

            // Hide entirely if no scrolling is possible.
            if (maxScroll <= 0) {
                thumbRef.current.style.display = "none";
                return;
            }
            thumbRef.current.style.display = "block";

            const availableSpacePercent = 100 - thumbSizePercent;
            const positionPercent = progress * availableSpacePercent;

            if (isVertical) {
                thumbRef.current.style.height = `${thumbSizePercent}%`;
                thumbRef.current.style.top = `${positionPercent}%`;
                thumbRef.current.style.width = "100%";
                thumbRef.current.style.left = "0";
                thumbRef.current.style.transform = "none";
            } else {
                thumbRef.current.style.width = `${thumbSizePercent}%`;
                thumbRef.current.style.left = `${positionPercent}%`;
                thumbRef.current.style.height = "100%";
                thumbRef.current.style.top = "0";
                thumbRef.current.style.transform = "none";
            }
        },
        [getMetrics, isVertical],
    );

    /** Sync the thumb with the carousel's current scroll position. */
    const updateThumb = useCallback(() => {
        if (!carouselApi) return;

        if (isPercentageMode) {
            applyThumbStyle(carouselApi.scrollProgress());
            return;
        }

        const snaps = carouselApi.scrollSnapList();
        const maxIndex = snaps.length - 1;
        if (maxIndex <= 0) {
            applyThumbStyle(0);
            return;
        }
        applyThumbStyle(carouselApi.selectedScrollSnap() / maxIndex);
    }, [carouselApi, isPercentageMode, applyThumbStyle]);

    // Subscribe to Embla events.
    useEffect(() => {
        if (!carouselApi) return;

        updateThumb();
        const drivingEvent = isPercentageMode ? "scroll" : "select";
        carouselApi.on("reInit", updateThumb);
        carouselApi.on(drivingEvent, updateThumb);

        return () => {
            carouselApi.off("reInit", updateThumb);
            carouselApi.off("scroll", updateThumb);
            carouselApi.off("select", updateThumb);
        };
    }, [carouselApi, updateThumb, isPercentageMode]);

    /** Programmatically scroll the carousel to a normalized progress value. */
    const scrollToProgress = useCallback(
        (progress: number) => {
            if (!carouselApi) return;

            const engine = carouselApi.internalEngine();
            const allowedProgress = clamp01(progress);

            engine.animation.stop();
            engine.scrollBody.useDuration(0);

            if (typeof (engine.limit as any).measure === "function") {
                const scrollBound = (engine.limit as any).measure(allowedProgress);
                engine.scrollTo.distance(scrollBound - engine.location.get(), false);
            } else {
                const { limit, target, scrollProgress, scrollTo } = engine;
                const distance = (allowedProgress - scrollProgress.get(target.get())) * limit.length * -1;
                engine.scrollTo.distance(distance, false);
            }

            engine.animation.start();
            applyThumbStyle(allowedProgress);
        },
        [carouselApi, applyThumbStyle],
    );

    // ─── Drag handling ────────────────────────────────────────────────────────

    const dragState = useRef<{ startPos: number; startProgress: number } | null>(null);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!isDraggable || !carouselApi) return;
            e.preventDefault();
            e.stopPropagation();

            dragState.current = {
                startPos: isVertical ? e.clientY : e.clientX,
                startProgress: clamp01(carouselApi.scrollProgress()),
            };
        },
        [isDraggable, carouselApi, isVertical],
    );

    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            if (!dragState.current) return;
            const metrics = getMetrics();
            if (!metrics) return;

            const currentPos = isVertical ? e.clientY : e.clientX;
            const deltaPx = currentPos - dragState.current.startPos;

            // Convert raw pixel delta to a progress delta.
            const deltaProgress = metrics.draggableSpacePx > 0 ? deltaPx / metrics.draggableSpacePx : 0;
            const newProgress = clamp01(dragState.current.startProgress + deltaProgress);

            scrollToProgress(newProgress);
        };

        const handleUp = () => {
            dragState.current = null;
        };

        document.addEventListener("mousemove", handleMove);
        document.addEventListener("mouseup", handleUp);

        return () => {
            document.removeEventListener("mousemove", handleMove);
            document.removeEventListener("mouseup", handleUp);
        };
    }, [getMetrics, isVertical, scrollToProgress]);

    // ─── Click-on-track to jump ───────────────────────────────────────────────

    const handleTrackClick = useCallback(
        (e: React.MouseEvent) => {
            const metrics = getMetrics();
            if (!metrics || !carouselApi) return;

            const clickOffsetPx = isVertical ? e.clientY - metrics.trackRect.top : e.clientX - metrics.trackRect.left;

            // Land the center of the thumb on the cursor.
            const targetPx = clickOffsetPx - metrics.thumbSizePx / 2;
            const position = metrics.draggableSpacePx > 0 ? clamp01(targetPx / metrics.draggableSpacePx) : 0;

            if (isPercentageMode) {
                scrollToProgress(position);
            } else {
                const snaps = carouselApi.scrollSnapList();
                carouselApi.scrollTo(Math.round(position * (snaps.length - 1)));
            }
        },
        [carouselApi, isVertical, getMetrics, isPercentageMode, scrollToProgress],
    );

    return (
        <StyledWrapper
            ref={trackRef}
            className={`carousel-scrollbar carousel-scrollbar-${axis}`}
            onClick={handleTrackClick}
        >
            <StyledWrapper
                ref={thumbRef}
                className="carousel-scrollbar-thumb"
                onMouseDown={handleMouseDown}
                onClick={stopPropagation}
                role="slider"
                aria-label="Carousel scrollbar"
            />
        </StyledWrapper>
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export default Scrollbar;
